import { useCallback, useEffect, useState } from 'react';
import {
  LayoutDashboard,
  Briefcase,
  Landmark,
  Mail,
  FileCheck2,
  ShieldAlert,
  Clock,
  Loader2,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  CircleDashed,
} from 'lucide-react';
import { apiFetch } from './apiBase';
import FieldRecon from './FieldRecon';
import SslValidator from './SslValidator';
import EffectiveDateCheck from './EffectiveDateCheck';
import TimeRecon from './TimeRecon';

const TABS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'employment', label: 'Employment', icon: Briefcase },
  { id: 'bank', label: 'Banking', icon: Landmark },
  { id: 'email', label: 'Email / ESS', icon: Mail },
  { id: 'ssl', label: 'SSL Intake', icon: FileCheck2 },
  { id: 'effdate', label: 'Effective Dates', icon: ShieldAlert },
  { id: 'time', label: 'Time & Labor', icon: Clock },
];

export default function PrePayroll({ onNavigate }) {
  const [tab, setTab] = useState('overview');

  return (
    <div className="space-y-6">
      {tab === 'overview' ? null : (
        <button
          type="button"
          onClick={() => setTab('overview')}
          className="text-xs text-cyan-400 hover:text-cyan-300"
        >
          ← Back to pre-payroll overview
        </button>
      )}

      <div className="flex flex-wrap gap-1.5 border-b border-slate-700/70 pb-2">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition ${
              tab === id
                ? 'bg-cyan-600/20 text-cyan-300 ring-1 ring-cyan-600/40'
                : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <Overview onOpen={setTab} onNavigate={onNavigate} />}
      {tab === 'employment' && (
        <FieldRecon
          category="employment"
          title="Employment / status reconciliation"
          description="Validate employment status, org assignment, and salary basis (salary vs hourly, FTE, pay area) between Oracle and ADP before payroll."
          oracleHint="Person number plus employment fields (status, employee group, salary, FTE)"
          adpHint="Global ID plus employment status, employee group, salary, and FTE"
        />
      )}
      {tab === 'bank' && (
        <FieldRecon
          category="bank"
          title="Banking / direct deposit reconciliation"
          description="Verify bank account and payment method between Oracle and ADP. Flags missing banking (issue a check), unsynced accounts, and check pay methods."
          oracleHint="Person number plus payment method, account, and bank branch"
          adpHint="Global ID plus bank account, bank key, and payment method"
        />
      )}
      {tab === 'email' && (
        <FieldRecon
          category="email"
          title="Email / ESS sync reconciliation"
          description="Confirm personal email and ESS user IDs are synced from Oracle to ADP. Flags emails present in Oracle but missing in ADP (pending SSL/ESS load)."
          oracleHint="Person number plus work email"
          adpHint="Global ID plus ESS / communication user ID"
        />
      )}
      {tab === 'ssl' && <SslValidator />}
      {tab === 'effdate' && <EffectiveDateCheck />}
      {tab === 'time' && <TimeRecon />}
    </div>
  );
}

function Overview({ onOpen, onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch('/api/prepayroll/dashboard')
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const tabForCheck = {
    payroll: 'recon',
    demographics: 'demographics',
    employment: 'employment',
    bank: 'bank',
    email: 'email',
    ssl: 'ssl',
    time: 'time',
  };

  return (
    <div className="space-y-6">
      <header className="page-hero">
        <div className="flex items-start justify-between gap-3">
          <div className="max-w-2xl">
            <h2 className="text-2xl font-bold tracking-tight text-slate-100">Pre-payroll readiness</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">
              Run each validation before payroll close. Green means the last run had no exceptions;
              amber shows how many issues need review.
            </p>
          </div>
          <button type="button" onClick={load} className="btn-secondary shrink-0 py-1.5 text-sm">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </header>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading status…
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(data?.checks || []).map((c) => {
            const target = tabForCheck[c.id];
            const isExternal = target === 'recon' || target === 'demographics';
            const clickable = !!target;
            const status = !c.hasRun ? 'idle' : c.exceptionCount > 0 ? 'warn' : 'ok';
            const StatusIcon =
              status === 'idle' ? CircleDashed : status === 'warn' ? AlertTriangle : CheckCircle2;
            const tone =
              status === 'idle'
                ? 'text-slate-500'
                : status === 'warn'
                  ? 'text-amber-400'
                  : 'text-emerald-400';
            return (
              <button
                key={c.id}
                type="button"
                onClick={
                  clickable
                    ? () => (isExternal ? onNavigate?.(target) : onOpen(target))
                    : undefined
                }
                className={`card-panel p-4 text-left transition ${
                  clickable ? 'hover:ring-1 hover:ring-cyan-600/40' : 'cursor-default opacity-90'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-slate-100">{c.label}</span>
                  <StatusIcon className={`h-4 w-4 shrink-0 ${tone}`} />
                </div>
                <div className="mt-2 text-xs text-slate-400">
                  {!c.hasRun ? (
                    <span>Not run yet</span>
                  ) : (
                    <>
                      <span className={status === 'warn' ? 'text-amber-400' : 'text-emerald-400'}>
                        {c.exceptionCount?.toLocaleString() ?? 0} exception(s)
                      </span>
                      {c.runAt && (
                        <span className="block text-slate-500">
                          {new Date(c.runAt).toLocaleString(undefined, {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          })}
                        </span>
                      )}
                    </>
                  )}
                  {c.payPeriod && <span className="block text-slate-500">Period {c.payPeriod}</span>}
                </div>
                {clickable && (
                  <span className="mt-2 block text-xs text-cyan-400">
                    {isExternal ? 'Open in sidebar →' : 'Open →'}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
